import { useEffect, useRef, useState } from 'react';
import type { TaskAttachment } from '../types';

interface FileTypeInfo {
  label: string;
  bg: string;
  fg: string;
}

/** Returns a short badge label + colors for a filename/contentType. */
function classifyFile(att: TaskAttachment): FileTypeInfo {
  const name = (att.filename ?? '').toLowerCase();
  const ct = (att.contentType ?? '').toLowerCase();
  const ext = name.includes('.') ? name.split('.').pop() ?? '' : '';

  if (ext === 'pdf' || ct === 'application/pdf') {
    return { label: 'PDF', bg: '#dc2626', fg: '#fff' };
  }
  if (ext === 'doc' || ext === 'docx' || ct.includes('wordprocessing') || ct.includes('msword')) {
    return { label: 'W', bg: '#2563eb', fg: '#fff' };
  }
  if (['xls', 'xlsx', 'csv', 'tsv'].includes(ext) || ct.includes('spreadsheet') || ct.includes('excel') || ct.includes('csv')) {
    return { label: 'X', bg: '#16a34a', fg: '#fff' };
  }
  if (['ppt', 'pptx', 'key'].includes(ext) || ct.includes('presentation') || ct.includes('powerpoint')) {
    return { label: 'P', bg: '#ea580c', fg: '#fff' };
  }
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'heic', 'svg'].includes(ext) || ct.startsWith('image/')) {
    return { label: '🖼', bg: '#0ea5e9', fg: '#fff' };
  }
  if (['zip', '7z', 'tar', 'gz', 'rar'].includes(ext) || ct.includes('zip') || ct.includes('compressed')) {
    return { label: 'ZIP', bg: '#7c3aed', fg: '#fff' };
  }
  if (['mp3', 'wav', 'm4a', 'flac', 'ogg'].includes(ext) || ct.startsWith('audio/')) {
    return { label: '♪', bg: '#db2777', fg: '#fff' };
  }
  if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext) || ct.startsWith('video/')) {
    return { label: '▶', bg: '#be123c', fg: '#fff' };
  }
  if (['txt', 'md', 'log'].includes(ext) || ct === 'text/plain' || ct === 'text/markdown') {
    return { label: 'TXT', bg: '#64748b', fg: '#fff' };
  }
  // Unknown / everything else
  return { label: ext.slice(0, 3).toUpperCase() || '?', bg: '#94a3b8', fg: '#0f172a' };
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface Props {
  attachments: TaskAttachment[];
  onDownload: (filename: string) => Promise<void>;
  downloadingName: string | null;
}

export function AttachmentPill({ attachments, onDownload, downloadingName }: Props) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const count = attachments.length;

  return (
    <div className="attachment-pill-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`attachment-pill ${open ? 'is-open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={`${count} attachment${count === 1 ? '' : 's'}`}
      >
        <span aria-hidden="true">📎</span>
        <span>{count} file{count === 1 ? '' : 's'}</span>
      </button>
      {open && (
        <div
          className="attachment-popover"
          role="dialog"
          aria-label="Attachments"
        >
          <ul>
            {attachments.map((a) => {
              const info = classifyFile(a);
              const isBusy = downloadingName === a.filename;
              return (
                <li key={a.filename}>
                  <button
                    type="button"
                    className="attachment-item"
                    onClick={() => void onDownload(a.filename)}
                    disabled={isBusy || !a.s3Key}
                    title={a.s3Key ? 'Download' : 'Not extracted'}
                  >
                    <span
                      className="attachment-glyph"
                      style={{ backgroundColor: info.bg, color: info.fg }}
                    >
                      {info.label}
                    </span>
                    <span className="attachment-name">{a.filename}</span>
                    <span className="attachment-size">{formatBytes(a.size)}</span>
                    <span className="attachment-dl" aria-hidden="true">
                      {isBusy ? '…' : '↓'}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
